import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../../../hooks/useCalendar';
import { Availability } from '../../../types';
import { persistSlotUpdates } from '../../../utils/persistSlots';
import { useSlotDrag, DragMode, SlotKey } from '../../../hooks/useSlotDrag';
import { useIsDarkMode } from '../../../hooks/useDarkMode';
import { useHourFormat } from '../../../hooks/useHourFormat';
import { getHeatColor } from '../../../utils/heatmap';
import { formatHourLabel, formatSlotAriaLabel, formatWeekdayLong } from '../../../utils/locale';
import { isSlotPast } from '../../../utils/slotTime';
import { PastSlotOverlay } from '../PastSlotOverlay';
import { SlotPopover } from '../SlotPopover';
import { SlotYouMark } from '../SlotYouMark';
import { SlotCountBadge } from '../SlotCountBadge';
import { useSlotHoverPopover } from '../../../hooks/useSlotHoverPopover';
import { useVerticalScrollChaining } from '../../../hooks/useVerticalScrollChaining';

interface DayViewProps {
  roomId: string;
  participantName: string;
  availability: Availability[];
  dayDate: string;
  weekStartDate: Date;
  visibleDayCount?: number;
  browserFingerprint: string;
  onDrawModeChange?: (active: boolean) => void;
  onNameReassigned?: (name: string) => void;
  onRateLimited?: () => void;
  totalParticipants?: number;
}

export function DayView({
  roomId,
  participantName,
  availability,
  dayDate,
  weekStartDate,
  visibleDayCount = 7,
  browserFingerprint,
  onDrawModeChange,
  onNameReassigned,
  onRateLimited,
  totalParticipants,
}: DayViewProps) {
  const { t, i18n } = useTranslation();
  const { hourFormat } = useHourFormat();
  const scrollRef = useRef<HTMLDivElement>(null);
  useVerticalScrollChaining(scrollRef);
  const currentHourRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const check = () => setIsMobileView(window.innerWidth <= 500);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const { timeSlots, setSlotsAvailability, isSlotSelected, getSlotParticipants } = useCalendar(
    availability,
    participantName,
    weekStartDate,
    visibleDayCount
  );

  const dayMeta = timeSlots.days.find(d => d.date === dayDate);
  const now = new Date();
  const isToday =
    dayDate ===
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const maxCount = useMemo(() => {
    let max = 0;
    timeSlots.hours.forEach(h => {
      const c = getSlotParticipants(dayDate, h.hour).length;
      if (c > max) max = c;
    });
    return max;
  }, [timeSlots.hours, dayDate, availability, getSlotParticipants]);

  const dark = useIsDarkMode();

  useEffect(() => {
    hasScrolledRef.current = false;
  }, [dayDate]);

  useEffect(() => {
    if (!hasScrolledRef.current && scrollRef.current && currentHourRef.current && isToday) {
      setTimeout(() => {
        const container = scrollRef.current;
        const el = currentHourRef.current;
        if (container && el) {
          const containerTop = container.getBoundingClientRect().top;
          const elementTop = el.getBoundingClientRect().top;
          container.scrollTop = container.scrollTop + (elementTop - containerTop - 100);
          hasScrolledRef.current = true;
        }
      }, 60);
    }
  }, [dayDate, isToday, timeSlots]);

  const commitSlots = useCallback(
    async (mode: DragMode, slots: SlotKey[], phase: 'preview' | 'persist' = 'persist') => {
      const available = mode === 'paint';
      const eligible = slots.filter(s => !isSlotPast(s.day, s.hour));
      if (eligible.length === 0) return;

      if (phase === 'preview') {
        const toChange = eligible.filter(s => isSlotSelected(s.day, s.hour) !== available);
        if (toChange.length === 0) return;
        setSlotsAvailability(toChange, available);
        return;
      }

      const isSlotOnServer = (day: string, hour: number) =>
        availability.some(
          a =>
            a.participant === participantName &&
            a.day === day &&
            a.hour === hour &&
            a.available
        );

      const toChange = eligible.filter(s => isSlotOnServer(s.day, s.hour) !== available);
      if (toChange.length === 0) return;
      setSlotsAvailability(toChange, available);
      const { failed, rateLimited } = await persistSlotUpdates({
        roomId,
        participantName,
        browserFingerprint,
        slots: toChange,
        available,
        onNameReassigned,
      });
      if (rateLimited) onRateLimited?.();
      if (failed.length) setSlotsAvailability(failed, !available);
    },
    [
      availability,
      browserFingerprint,
      isSlotSelected,
      onNameReassigned,
      onRateLimited,
      participantName,
      roomId,
      setSlotsAvailability,
    ]
  );

  const { handlers: dragHandlers, isDrawMode } = useSlotDrag({
    isSelected: isSlotSelected,
    onCommit: commitSlots,
    isSlotDisabled: isSlotPast,
    activation: isMobileView ? 'longPress' : 'immediate',
    onDrawModeChange,
  });

  const {
    popover,
    setPopover,
    openPopoverFor,
    handleSlotMouseEnter,
    handleSlotMouseMove,
    handleSlotMouseLeave,
    handleCalendarMouseLeave,
  } = useSlotHoverPopover(
    isMobileView,
    useCallback(
      (day, hour) => getSlotParticipants(day, hour).length > 0,
      [getSlotParticipants]
    )
  );

  if (!dayMeta) return null;

  const popoverParticipants = popover ? getSlotParticipants(popover.day, popover.hour) : [];
  const popoverTitle = popover
    ? (() => {
        const [yy, mm, dd] = popover.day.split('-').map(Number);
        const d = new Date(yy, mm - 1, dd);
        const wd = formatWeekdayLong(d, i18n.language);
        return `${wd}, ${formatHourLabel(popover.hour, i18n.language, hourFormat)}`;
      })()
    : '';

  return (
    <div className="w-full min-w-0">
      <div
        className="overflow-hidden rounded-xl border border-room-border"
        onMouseLeave={handleCalendarMouseLeave}
      >
      <div
        ref={scrollRef}
        data-day-scroll
        className={clsx(
          'calendar-scroll-region relative max-h-[480px] transition-shadow duration-150',
          isDrawMode && 'ring-2 ring-inset ring-room-accent/40'
        )}
        style={{ touchAction: 'pan-y' }}
      >
        {isDrawMode ? (
          <div className="pointer-events-none absolute right-1.5 top-1.5 z-10 flex items-center gap-1 rounded-full bg-room-surface/90 px-2 py-0.5 text-[10px] font-medium text-room-accent shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-room-accent" />
            {t('calendar.drawMode')}
          </div>
        ) : null}
        <div
          onPointerDown={dragHandlers.onPointerDown}
          onPointerMove={dragHandlers.onPointerMove}
          onPointerUp={dragHandlers.onPointerUp}
          onPointerCancel={dragHandlers.onPointerCancel}
          style={{ touchAction: isDrawMode ? 'none' : undefined }}
        >
        {timeSlots.hours.map(hour => {
          const participants = getSlotParticipants(dayDate, hour.hour);
          const count = participants.length;
          const selected = isSlotSelected(dayDate, hour.hour);
          const isPast = isSlotPast(dayDate, hour.hour);
          const isCurrentHour = isToday && hour.hour === now.getHours();
          const pct = isCurrentHour ? (now.getMinutes() / 60) * 100 : 0;
          const heat = getHeatColor(count, Math.max(maxCount, 1), dark);
          const ariaLabel = formatSlotAriaLabel({
            day: dayDate,
            hour: hour.hour,
            count,
            total: totalParticipants,
            lang: i18n.language,
            hourFormat,
            t,
          });

          return (
            <div
              key={hour.hour}
              ref={isCurrentHour ? currentHourRef : null}
              className="grid grid-cols-[52px_1fr] border-b border-room-border"
              style={{ minHeight: 56 }}
            >
              <div className={clsx('pt-1 pr-2.5 text-right text-[9px] text-room-text-dim', isPast && 'opacity-50')}>
                {hour.hour > 0 ? <span className="font-medium">{hour.label}</span> : null}
              </div>
              <button
                type="button"
                disabled={isPast}
                data-slot-day={dayDate}
                data-slot-hour={hour.hour}
                aria-pressed={selected}
                aria-disabled={isPast}
                aria-label={ariaLabel}
                onMouseEnter={e => handleSlotMouseEnter(e, dayDate, hour.hour)}
                onMouseMove={e => handleSlotMouseMove(e, dayDate, hour.hour)}
                onMouseLeave={() => handleSlotMouseLeave(dayDate, hour.hour)}
                className={clsx(
                  'relative border-l border-room-border px-2 py-1 text-left transition-colors',
                  isPast
                    ? 'cursor-not-allowed'
                    : 'hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-room-accent'
                )}
                style={{
                  backgroundColor: count > 0 ? heat : undefined,
                  touchAction: isDrawMode ? 'none' : isMobileView ? 'pan-y' : undefined,
                }}
              >
                {isPast ? <PastSlotOverlay /> : null}
                {isCurrentHour ? (
                  <div
                    className="pointer-events-none absolute left-0 right-0 z-[5]"
                    style={{ top: `${pct}%` }}
                  >
                    <div className="relative h-[1.5px] bg-[#E24B4A]">
                      <div className="absolute -left-[3.5px] -top-[2.5px] h-[7px] w-[7px] rounded-full bg-[#E24B4A]" />
                    </div>
                  </div>
                ) : null}
                {selected ? <SlotYouMark hasHeat={count > 0} size={16} /> : null}
                <SlotCountBadge
                  count={count}
                  size="md"
                  onTap={
                    isMobileView && count > 0
                      ? e => {
                          const slotEl = (e.currentTarget as HTMLElement).closest<HTMLElement>(
                            '[data-slot-day][data-slot-hour]'
                          );
                          if (!slotEl) return;
                          openPopoverFor(slotEl, dayDate, hour.hour, {
                            x: e.clientX,
                            y: e.clientY,
                          });
                        }
                      : undefined
                  }
                />
              </button>
            </div>
          );
        })}
        </div>
      </div>
      </div>

      {popover ? (
        <SlotPopover
          anchor={popover.rect}
          cursor={popover.cursor}
          title={popoverTitle}
          subtitle={t('calendar.slot.available', { count: popoverParticipants.length })}
          participants={popoverParticipants}
          currentUser={participantName}
          isMobile={isMobileView}
          onClose={() => setPopover(null)}
          onToggle={
            isMobileView
              ? () => {
                  const wasSelected = isSlotSelected(popover.day, popover.hour);
                  commitSlots(wasSelected ? 'clear' : 'paint', [
                    { day: popover.day, hour: popover.hour },
                  ]);
                }
              : undefined
          }
          toggleLabel={t('calendar.slot.toggleAvailability')}
        />
      ) : null}
    </div>
  );
}
